import React from 'react';
import { FormikErrors, FormikHandlers, FormikTouched } from 'formik';
import { InputLabel, Spacer } from 'src/common';
import AutoGrowingInput from 'src/components/form/AutoGrowingInput';
import MaterialInput from 'src/components/form/MaterialInput';
import { GenericSelect } from 'src/components/select/GenericSelect';
import { RoleSelect } from 'src/components/select';
import { formatDate } from 'src/utils/misc';
import { mpEvent, MPEvents } from 'src/utils/mixpanel';
import { Role } from 'src/graphql/_generated';
import * as S from '../UserModal.styles';
import { InitValues, SelectedOrgUnit } from '../types';
import { PBA_EXISTING_PERMISSIONS_REASON } from '../../constants';

interface UserFormFieldsProps {
  initError: string;
  selectedOrgUnit?: SelectedOrgUnit;
  values: InitValues;
  handleChange: FormikHandlers['handleChange'];
  handleBlur: FormikHandlers['handleBlur'];
  touched: FormikTouched<InitValues>;
  errors: FormikErrors<InitValues>;
  setFieldValue: (field: string, value: unknown, shouldValidate?: boolean) => void;
  canEditUser?: boolean;
  highlightRoles?: boolean;
  isOwnProfile: boolean;
  isPlatformAdmin: boolean;
  isPartnerAdmin: boolean;
}

export const UserFormFields: React.FC<UserFormFieldsProps> = ({
  initError,
  selectedOrgUnit,
  values,
  handleChange,
  handleBlur,
  touched,
  errors,
  setFieldValue,
  canEditUser,
  highlightRoles,
  isOwnProfile,
  isPlatformAdmin,
  isPartnerAdmin,
}) => {
  const memberActions = selectedOrgUnit?.member?.memberActions;

  const getPermissionBasedDisabledOptions = (recordType: 'O' | 'W'): { value: Role; reason: string }[] => {
    const disabledOptions: { value: Role; reason: string }[] = [];
    const insufficientPermissionsReason = 'Insufficent permissions. Contact your admin for access.';

    if (recordType === 'O') {
      if (!memberActions?.canMakeAdmin) {
        disabledOptions.push({ value: Role.OrganisationAdmin, reason: insufficientPermissionsReason });
      }
      if (!memberActions?.canMakePartnerAdmin && values.role !== Role.PartnerBranchAdmin) {
        disabledOptions.push({
          value: Role.PartnerBranchAdmin,
          reason: isPlatformAdmin || isPartnerAdmin ? PBA_EXISTING_PERMISSIONS_REASON : insufficientPermissionsReason,
        });
      }
    } else if (recordType === 'W') {
      if (!memberActions?.canMakeAdmin) {
        disabledOptions.push({ value: Role.WorkspaceAdmin, reason: insufficientPermissionsReason });
      }
    }

    return disabledOptions;
  };

  const notifications = () => {
    const canEdit = isOwnProfile || canEditUser;
    return (
      <>
        <Spacer $px={20} />
        <InputLabel>DAILY DIGEST NOTIFICATIONS</InputLabel>
        <GenericSelect
          disabled={!canEdit}
          onChange={(value) => {
            const newValue = value.value === 'true';
            setFieldValue('receiveNotifications', newValue);
            mpEvent(MPEvents.NotificationToggle, { enabled: newValue });
          }}
          options={[
            { label: 'On', value: 'true' },
            { label: 'Off', value: 'false' },
          ]}
          value={values.receiveNotifications ? 'true' : 'false'}
        />
      </>
    );
  };

  if (initError) {
    return (
      <>
        <Spacer $px={32} />
        <div className="error error--left">{initError}</div>
      </>
    );
  }

  return (
    <>
      {selectedOrgUnit?.recordType === 'O' && (
        <div className="contents">
          <div>
            <InputLabel>NAME</InputLabel>
            <AutoGrowingInput
              id="name"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.name}
              fullWidth
              error={touched.name && Boolean(errors.name)}
              helperText={touched.name && errors.name}
              data-cy="name-input"
              disabled={!canEditUser}
            />
            <Spacer $px={20} />
            <InputLabel>ASSIGNED ROLE IN {selectedOrgUnit?.name.toUpperCase()}</InputLabel>
            <S.RoleHighlight $active={!!highlightRoles}>
              <RoleSelect
                disabled={!canEditUser}
                onChange={(value) => setFieldValue('role', value.value)}
                value={values.role}
                disabledOptions={[
                  {
                    value: Role.WorkspaceAdmin,
                    reason: `This is a workspace-level role. Switch to a workspace using the dropdown above to assign this role to the user.`,
                  },
                  ...getPermissionBasedDisabledOptions('O'),
                ]}
                filteredOutRoles={[Role.CustomerManager]}
                optionTooltips={[
                  {
                    value: Role.NoAccess,
                    reason:
                      "When setting a users' permission to blocked the user will no longer be able to access the organisation",
                  },
                ]}
              />
            </S.RoleHighlight>
          </div>
          <div>
            <InputLabel>EMAIL ADDRESS</InputLabel>
            <MaterialInput value={values?.email} fullWidth disabled />
          </div>
        </div>
      )}
      {selectedOrgUnit?.recordType === 'W' && (
        <div className="contents">
          <div>
            <InputLabel>NAME</InputLabel>
            <AutoGrowingInput
              id="name"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.name}
              fullWidth
              error={touched.name && Boolean(errors.name)}
              helperText={touched.name && errors.name}
              data-cy="name-input"
              disabled={!canEditUser}
            />
            <Spacer $px={20} />
            <InputLabel>ASSIGNED ROLE IN {selectedOrgUnit?.name.toUpperCase()}</InputLabel>
            <S.RoleHighlight $active={!!highlightRoles}>
              <RoleSelect
                disabled={!canEditUser}
                onChange={(value) => setFieldValue('role', value.value)}
                value={values.role}
                disabledOptions={[
                  {
                    value: Role.PartnerBranchAdmin,
                    reason:
                      'This is an organisation-level role. Switch to parent organisation using the dropdown above to assign this role to the user.',
                  },
                  {
                    value: Role.OrganisationAdmin,
                    reason: `This is an organisation-level role. Switch to parent organisation using the dropdown above to assign this role to the user.`,
                  },
                  ...getPermissionBasedDisabledOptions('W'),
                ]}
                filteredOutRoles={[Role.CustomerManager]}
                optionTooltips={[
                  {
                    value: Role.NoAccess,
                    reason:
                      "When setting a users' permission to blocked the user will no longer be able to access the workspace",
                  },
                ]}
              />
            </S.RoleHighlight>
            {notifications()}
          </div>

          <div>
            <InputLabel>EMAIL ADDRESS</InputLabel>
            <MaterialInput value={values?.email} fullWidth disabled />
            <Spacer $px={20} />
            <InputLabel>JOIN DATE</InputLabel>
            <MaterialInput value={formatDate(values.acceptedDate)} fullWidth disabled />
          </div>
        </div>
      )}
      {!selectedOrgUnit && (
        <div className="contents">
          <div>
            <InputLabel>NAME</InputLabel>
            <AutoGrowingInput
              id="name"
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.name}
              fullWidth
              error={touched.name && Boolean(errors.name)}
              helperText={touched.name && errors.name}
              data-cy="name-input"
            />
            {notifications()}
          </div>
          <div>
            <InputLabel>EMAIL ADDRESS</InputLabel>
            <MaterialInput value={values?.email} fullWidth disabled />
          </div>
        </div>
      )}
    </>
  );
};
